import React, { useCallback, useEffect, useState } from "react";
import { api, API, apiError } from "@/lib/api";
import { fmtDateTime, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable } from "@/components/shared";
import { Card, run } from "@/components/agent/AgentPanels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlugZap, Plus, Trash2, RotateCcw, Save } from "lucide-react";

const STATE = {
  not_connected: ["Not connected", "bg-slate-100 text-slate-700 border-slate-200"],
  credentials_added_untested: ["Credentials added — not yet tested", "bg-amber-100 text-amber-800 border-amber-200"],
  connected: ["Connected", "bg-emerald-100 text-emerald-800 border-emerald-200"],
};

function MetaStatus({ st, reload }) {
  const [label, cls] = STATE[st.state] || STATE.not_connected;
  const test = () => api.post("/meta/test").then(({ data }) => { data.ok ? toast.success("Meta token works") : toast.error(data.error || "Test failed"); reload(); }).catch((ex) => toast.error(apiError(ex.response?.data?.detail)));
  return (
    <Card title="Meta Lead Ads (Facebook + Instagram)" actions={<Button size="sm" variant="outline" onClick={test} disabled={st.state === "not_connected"} data-testid="meta-test-btn">Test connection</Button>}>
      <span className={`inline-flex border rounded-full px-3 py-1 text-xs font-semibold ${cls}`} data-testid="meta-status-badge">{label}</span>
      {st.missing_credentials?.length > 0 && (
        <p className="text-sm text-muted-foreground mt-3" data-testid="meta-missing">Waiting for: {st.missing_credentials.join(", ")}. Leads can't sync until your Meta app is authorised and these are added securely.</p>
      )}
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mt-3">
        <div>Webhook URL: <span className="font-mono text-xs break-all" data-testid="meta-webhook-url">{API}/meta/webhook</span></div>
        <div>Webhook verified: {st.webhook_verified_at ? fmtDateTime(st.webhook_verified_at) : "No"}</div>
        <div>Last test: {st.last_test_at ? `${st.last_test_ok ? "OK" : "Failed"} · ${fmtDateTime(st.last_test_at)}` : "Never"}</div>
        <div>Last successful sync: {st.last_successful_sync ? fmtDateTime(st.last_successful_sync) : "Never"}</div>
        <div data-testid="meta-event-counts">Events — pending {st.events?.pending || 0} · processed {st.events?.processed || 0} · failed {st.events?.failed || 0}</div>
        {st.last_test_error && <div className="text-rose-600">Error: {st.last_test_error}</div>}
      </div>
    </Card>
  );
}

const fmToStr = (fm) => Object.entries(fm || {}).map(([k, v]) => `${k}=${v}`).join(", ");

function FormMapping({ products, users }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/meta/config").then(({ data }) => setCfg({ ...data, forms: data.forms.map((f) => ({ ...f, field_map: fmToStr(f.field_map) })) })); }, []);
  if (!cfg) return null;
  const upd = (i, k, v) => setCfg((c) => ({ ...c, forms: c.forms.map((f, j) => (j === i ? { ...f, [k]: v } : f)) }));
  const save = () => run(() => api.put("/meta/config", cfg), "Meta mapping saved");
  const productOpts = (b) => (b === "loan" ? products.loan : products.insurance);
  return (
    <Card title="Lead Form → Product Mapping" actions={<>
      <Button size="sm" variant="outline" onClick={() => setCfg((c) => ({ ...c, forms: [...c.forms, { form_id: "", business: "loan", field_map: "" }] }))} data-testid="meta-add-form-btn"><Plus className="h-4 w-4 mr-1" />Add form</Button>
      <Button size="sm" onClick={save} data-testid="meta-save-config-btn"><Save className="h-4 w-4 mr-1" />Save</Button>
    </>}>
      <div className="mb-3 w-64"><Input placeholder="Facebook Page ID" value={cfg.page_id || ""} onChange={(e) => setCfg({ ...cfg, page_id: e.target.value })} data-testid="meta-page-id" /></div>
      {cfg.forms.length === 0 && <p className="text-sm text-muted-foreground">No forms mapped yet. Leads from unmapped forms are logged as failed, never guessed.</p>}
      <div className="space-y-3">
        {cfg.forms.map((f, i) => (
          <div key={i} className="grid md:grid-cols-6 gap-2 items-center rounded-lg border border-border p-3" data-testid="meta-form-row">
            <Input placeholder="Meta Form ID" value={f.form_id} onChange={(e) => upd(i, "form_id", e.target.value)} data-testid="meta-form-id" />
            <Input placeholder="Form name" value={f.form_name || ""} onChange={(e) => upd(i, "form_name", e.target.value)} />
            <Select value={f.business} onValueChange={(v) => { upd(i, "business", v); upd(i, "product", ""); }}><SelectTrigger data-testid="meta-form-business"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="loan">Loan</SelectItem><SelectItem value="insurance">Insurance</SelectItem></SelectContent></Select>
            <Select value={f.product || ""} onValueChange={(v) => upd(i, "product", v)}><SelectTrigger data-testid="meta-form-product"><SelectValue placeholder="Product *" /></SelectTrigger>
              <SelectContent>{productOpts(f.business).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            <Select value={f.default_assignee || "auto"} onValueChange={(v) => upd(i, "default_assignee", v === "auto" ? null : v)}><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="auto">Area rule / unassigned</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
            <Button size="icon" variant="ghost" className="text-rose-600 justify-self-end" onClick={() => setCfg((c) => ({ ...c, forms: c.forms.filter((_, j) => j !== i) }))} data-testid="meta-remove-form-btn"><Trash2 className="h-4 w-4" /></Button>
            <Input className="md:col-span-6 font-mono text-xs" placeholder="Extra field mapping, e.g. monthly_income=monthly_income, company=company (full_name, phone_number, email, city, zip_code are automatic)"
              value={f.field_map || ""} onChange={(e) => upd(i, "field_map", e.target.value)} data-testid="meta-form-fieldmap" />
          </div>
        ))}
      </div>
    </Card>
  );
}

function EventLog({ reloadStatus }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/meta/events").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const cols = [
    { key: "leadgen_id", header: "Meta Lead ID", render: (r) => <span className="font-mono text-xs">{r.leadgen_id}</span> },
    { key: "form_id", header: "Form", render: (r) => <span className="text-xs">{r.form_id}</span> },
    { key: "received_at", header: "Received", render: (r) => fmtDateTime(r.received_at) },
    { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status === "processed" ? "verified" : r.status} /> },
    { key: "crm_ref", header: "CRM record", render: (r) => r.crm_ref ? `${r.crm_ref} (${titleCase(r.business)})` : <span className="text-xs text-rose-600">{r.error}</span> },
    { key: "attempts", header: "Tries", render: (r) => `${r.attempts}${r.duplicate_deliveries ? ` · ${r.duplicate_deliveries} dup` : ""}` },
    { key: "a", header: "", render: (r) => r.status === "failed" && <Button size="sm" variant="outline" className="h-7" data-testid="meta-retry-btn" onClick={() => run(() => api.post(`/meta/events/${r.id}/retry`), "Retry queued").then(() => { load(); reloadStatus(); })}><RotateCcw className="h-3.5 w-3.5 mr-1" />Retry</Button> },
  ];
  return <Card title="Sync History"><DataTable columns={cols} rows={rows} rowTestId="meta-event-row" emptyText="No Meta lead events received yet" /></Card>;
}

function WebsiteFunnel() {
  const [f, setF] = useState(null);
  useEffect(() => { api.get("/reports/website-funnel").then(({ data }) => setF(data)).catch(() => {}); }, []);
  if (!f) return null;
  const steps = [["Form opened", f.form_opened], ["Submitted", f.form_submitted], ["Lead saved", f.lead_saved], ["Sent to lender", f.redirect_initiated]];
  return (
    <Card title="Website Loan Form (₹1 lakh) — Funnel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {steps.map(([l, n]) => <div key={l} className="rounded-lg border border-border p-3"><div className="text-xs text-muted-foreground">{l}</div><div className="text-xl font-bold" data-testid={`funnel-${l.toLowerCase().replace(/\s+/g, "-")}`}>{n}</div></div>)}
      </div>
      <p className="text-xs text-muted-foreground mt-3">"Sent to lender" means the customer was redirected. It does not mean the lender application was completed, approved or disbursed.</p>
      {f.by_campaign.length > 0 && <div className="text-sm mt-3">By campaign: {f.by_campaign.map((c) => `${c.campaign} (${c.leads})`).join(" · ")}</div>}
    </Card>
  );
}

function Messaging() {
  const [st, setSt] = useState(null);
  const [tpl, setTpl] = useState({});
  const [log, setLog] = useState([]);
  const load = useCallback(() => {
    api.get("/messaging/status").then(({ data }) => { setSt(data); setTpl(data.templates); });
    api.get("/messaging/log").then(({ data }) => setLog(data));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!st) return null;
  const save = () => run(() => api.put("/messaging/templates", { items: tpl }), "Templates saved").then(load);
  const cols = [
    { key: "created_at", header: "When", render: (m) => fmtDateTime(m.created_at) },
    { key: "template", header: "Template", render: (m) => titleCase(m.template) },
    { key: "to", header: "To", render: (m) => <span className="font-mono text-xs">{m.to.slice(0, 5)}*****{m.to.slice(-3)}</span> },
    { key: "status", header: "Status", render: (m) => <span className="text-xs font-semibold" data-testid="msg-status">{m.status.replace(/_/g, " ")}</span> },
    { key: "error", header: "", render: (m) => <span className="text-xs text-rose-600">{m.error}</span> },
  ];
  return (
    <Card title="SMS & WhatsApp (Twilio)" actions={<Button size="sm" onClick={save} data-testid="msg-save-templates">Save templates</Button>}>
      <span className={`inline-flex border rounded-full px-3 py-1 text-xs font-semibold ${STATE[st.state === "not_connected" ? "not_connected" : "credentials_added_untested"][1]}`} data-testid="twilio-status-badge">
        {st.state === "not_connected" ? "Not connected" : "Credentials added — delivery shown only from Twilio status updates"}</span>
      {st.missing_credentials.length > 0 && <p className="text-sm text-muted-foreground mt-2" data-testid="twilio-missing">Waiting for: {st.missing_credentials.join(", ")}{!st.whatsapp_sender && " (WhatsApp also needs an approved TWILIO_WHATSAPP_FROM sender)"}. Until then messages are logged as "not sent, not connected". Indian SMS also needs DLT-registered templates.</p>}
      <div className="space-y-3 mt-3">
        {Object.entries(tpl).map(([k, t]) => (
          <div key={k} className="rounded-lg border border-border p-3 space-y-2" data-testid="msg-template-row">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-sm">{t.label}</span>
              <Select value={t.channel} onValueChange={(v) => setTpl({ ...tpl, [k]: { ...t, channel: v } })}><SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sms">SMS</SelectItem><SelectItem value="whatsapp">WhatsApp</SelectItem></SelectContent></Select>
              <label className="flex items-center gap-2 text-xs"><Switch checked={t.enabled} onCheckedChange={(v) => setTpl({ ...tpl, [k]: { ...t, enabled: v } })} />Enabled</label>
            </div>
            <Input value={t.body} onChange={(e) => setTpl({ ...tpl, [k]: { ...t, body: e.target.value } })} className="text-sm" data-testid={`msg-template-${k}`} />
          </div>
        ))}
        <p className="text-xs text-muted-foreground">Variables: {"{name}"}, {"{ref}"}, {"{time}"}. Opt-outs: {st.optouts} (customers who replied STOP never get messages).</p>
      </div>
      <div className="mt-3"><DataTable columns={cols} rows={log} rowTestId="msg-log-row" emptyText="No messages yet" /></div>
    </Card>
  );
}

export default function Integrations() {
  const [st, setSt] = useState(null);
  const [products, setProducts] = useState({ loan: [], insurance: ["Car Insurance", "Bike Insurance", "Health Insurance", "Life Insurance", "Travel Insurance"] });
  const [users, setUsers] = useState([]);
  const loadStatus = useCallback(() => api.get("/meta/status").then(({ data }) => setSt(data)), []);
  useEffect(() => {
    loadStatus();
    api.get("/loan/products").then(({ data }) => setProducts((p) => ({ ...p, loan: data.map((x) => x.name) }))).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.active !== false))).catch(() => {});
  }, [loadStatus]);
  return (
    <div className="space-y-4">
      <PageHeader title="Integrations" subtitle="Connection status is shown only from real tests and real synced events" icon={PlugZap} />
      {st && <MetaStatus st={st} reload={loadStatus} />}
      <FormMapping products={products} users={users} />
      <EventLog reloadStatus={loadStatus} />
      <Messaging />
      <WebsiteFunnel />
    </div>
  );
}
